/**
 * Why a self-schedule day column ended up with zero bookable slots.
 *
 * The pipeline collapses four unrelated situations into the same empty
 * array, so the day header explained all of them with "Interviewer is
 * not available on this day" — true for two, actively misleading for
 * the others (ENG-3642). A recruiter who reads it goes and checks the
 * interviewer's calendar, which is the one place that can neither
 * confirm nor deny a working-hours gap or a notice cutoff.
 *
 * Everything here is re-derived from inputs the hook already holds —
 * no extra fetch, no extra pipeline run.
 */
import moment from 'moment-timezone';
import { TODAY_BUFFER_MINUTES } from '../pipeline';
import type { InterviewKind, Interval } from '../../../availability';

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;

export type DayUnavailableReason =
  /** Working hours covering this date have not resolved (loading or failed). */
  | 'unknown'
  /** At least one interviewer has no working hours configured on this date. */
  | 'outside_availability'
  /** Panel only — everyone works this date, but never at the same time. */
  | 'no_overlap'
  /** Working hours exist, busy events consume all of them. */
  | 'calendar_busy'
  /** Free windows exist but all sit before the notice / today-buffer cutoff. */
  | 'too_soon'
  /** A window survives the cutoff, but nothing long enough fits in it. */
  | 'no_fit';

/** Week span whose working-hours fetch has RESOLVED. */
export interface AvailabilityWeekRange {
  startIso: string;
  endIso: string;
}

/**
 * `true` when a resolved week actually covers `dateKey`.
 *
 * This is what separates "the interviewer has nothing set that day"
 * from "we do not know yet". Asking `!isLoading` instead would be
 * wrong: an errored or disabled query also reports `isLoading: false`
 * with no data, and the day header would then state, as fact, that a
 * named colleague is unavailable.
 */
export function isDateCoveredByAvailabilityWeeks(
  weeks: AvailabilityWeekRange[] | undefined,
  dateKey: string,
  timezone: string
): boolean {
  if (!weeks?.length) return false;
  const dayStartMs = moment.tz(dateKey, 'YYYY-MM-DD', timezone).valueOf();
  return weeks.some((week) => {
    const startMs = moment.tz(week.startIso, timezone).valueOf();
    const endMs = moment.tz(week.endIso, timezone).valueOf();
    return dayStartMs >= startMs && dayStartMs < endMs;
  });
}

/**
 * `userId → set of YYYY-MM-DD keys that user has working hours on`.
 *
 * Panel days come out of the pipeline as the INTERSECTION, so "nobody
 * set hours" and "everybody set hours that never meet" are the same
 * empty day downstream. This is the only input that separates them.
 */
export function collectDaysWithHoursByUser(
  scheduleByUserId: Record<string, Interval[]> | undefined,
  timezone: string
): Record<string, Set<string>> {
  const out: Record<string, Set<string>> = {};
  for (const [userId, intervals] of Object.entries(scheduleByUserId ?? {})) {
    const days = new Set<string>();
    for (const iv of intervals ?? []) {
      const start = moment.tz(iv.start, timezone);
      const end = moment.tz(iv.end, timezone);
      if (!start.isValid() || !end.isValid()) continue;
      const cursor = start.clone().startOf('day');
      const lastDay = end.clone().startOf('day');
      // Exclusive end exactly at midnight belongs to the previous day.
      const stopBefore = end.isSame(lastDay)
        ? lastDay
        : lastDay.clone().add(1, 'day');
      while (cursor.isBefore(stopBefore)) {
        days.add(cursor.format('YYYY-MM-DD'));
        cursor.add(1, 'day');
      }
    }
    out[userId] = days;
  }
  return out;
}

/**
 * Which of `interviewerIds` have no working hours on `dateKey`.
 *
 * An id missing from `daysWithHoursByUser` counts as having none —
 * `useCommonAvailabilityRange` drops users whose fetched weeks came
 * back empty, and that absence is exactly the "this person set nothing"
 * signal the day header wants to name.
 */
export function interviewersWithoutHoursOnDay(
  interviewerIds: string[],
  daysWithHoursByUser: Record<string, Set<string>>,
  dateKey: string
): string[] {
  return interviewerIds.filter((id) => !daysWithHoursByUser[id]?.has(dateKey));
}

export interface ResolveDayUnavailableReasonArgs {
  timezone: string;
  /** Does a resolved availability week cover this date? */
  hasResolvedAvailability: boolean;
  /** Does any interviewer have working hours on this date? */
  hasWorkingHours: boolean;
  /**
   * Free windows for the date — `availability − busy`, taken BEFORE
   * minimum notice and the today buffer trim them, and WITHOUT the
   * generator's `duration` floor. Empty means the calendar swallowed
   * the whole working day; a short window left standing is what tells
   * `no_fit` apart from `calendar_busy`.
   */
  freeWindows: Interval[];
  minimumNoticeHours: number;
  nowMs: number;
  interviewKind?: InterviewKind;
  /**
   * Panel only — ids with no working hours on this date, from
   * `interviewersWithoutHoursOnDay`. An empty array is a positive
   * statement ("everyone works today"), which is what promotes the
   * empty intersection to `no_overlap`; `undefined` means we were
   * never told, so the wording stays with the older, safer answer.
   */
  interviewerIdsWithoutHours?: string[];
}

export function resolveDayUnavailableReason({
  timezone,
  hasResolvedAvailability,
  hasWorkingHours,
  freeWindows,
  minimumNoticeHours,
  nowMs,
  interviewKind,
  interviewerIdsWithoutHours,
}: ResolveDayUnavailableReasonArgs): DayUnavailableReason {
  if (!hasResolvedAvailability) return 'unknown';
  if (!hasWorkingHours) {
    return interviewKind === 'panel' && interviewerIdsWithoutHours?.length === 0
      ? 'no_overlap'
      : 'outside_availability';
  }
  if (freeWindows.length === 0) return 'calendar_busy';

  // Mirrors the generator: `applyTodayBuffer` floors every gap start at
  // `now + 30min` and `applyMinimumNotice` at `now + N hours`, whichever
  // lands later. Without the buffer term, the very case that opened this
  // ticket — the current day dying at 17:34 against an 18:00 close with
  // NO minimum notice configured — is misreported as `no_fit`.
  const cutoffMs = Math.max(
    nowMs + minimumNoticeHours * MS_PER_HOUR,
    nowMs + TODAY_BUFFER_MINUTES * MS_PER_MINUTE
  );
  const everyWindowBeforeCutoff = freeWindows.every(
    (window) => moment.tz(window.end, timezone).valueOf() <= cutoffMs
  );
  return everyWindowBeforeCutoff ? 'too_soon' : 'no_fit';
}
